import { TREND_COLORS } from '@/trendData/trendData.constants';
import { randomUUID } from '@seeqdev/utils';
import type {
  ReasonCodeLevel,
  ReasonCodeTreeNode,
} from '@/vantage/components/extensionConfig/reasonCodeManagement/reasonCode.types';

export type DropPosition = 'before' | 'after' | 'inside';

export type DropTarget = {
  id: string;
  position: DropPosition;
};

export const positionFor = (clientY: number, rect: DOMRect): DropPosition => {
  const offset = clientY - rect.top;
  return offset < rect.height * 0.28 ? 'before' : offset > rect.height * 0.72 ? 'after' : 'inside';
};

export const levelColor = (depth: number): string => TREND_COLORS[depth % TREND_COLORS.length];

export const generateNodeId = (): string => randomUUID();

export const makeNode = (name: string): ReasonCodeTreeNode => ({ id: generateNodeId(), name, children: [] });

export const cloneTree = (tree: ReasonCodeTreeNode[]): ReasonCodeTreeNode[] =>
  tree.map((node) => ({ ...node, children: cloneTree(node.children) }));

export const findNode = (tree: ReasonCodeTreeNode[], id: string): ReasonCodeTreeNode | null => {
  for (const node of tree) {
    if (node.id === id) return node;
    const found = findNode(node.children, id);
    if (found) return found;
  }
  return null;
};

export const removeNode = (
  tree: ReasonCodeTreeNode[],
  id: string,
): [ReasonCodeTreeNode[], ReasonCodeTreeNode | null] => {
  const out: ReasonCodeTreeNode[] = [];
  let removed: ReasonCodeTreeNode | null = null;
  for (const node of tree) {
    if (node.id === id) {
      removed = node;
    } else {
      const [children, childRemoved] = removeNode(node.children, id);
      if (childRemoved) removed = childRemoved;
      out.push({ ...node, children });
    }
  }
  return [out, removed];
};

/** True when `id` is in `node`'s subtree; blocks dropping a node into its own descendants. */
export const isDescendant = (node: ReasonCodeTreeNode, id: string): boolean => {
  for (const child of node.children) {
    if (child.id === id || isDescendant(child, id)) return true;
  }
  return false;
};

export const canDropOn = (tree: ReasonCodeTreeNode[], dragId: string, targetId: string): boolean => {
  if (dragId === targetId) return false;
  const dragged = findNode(tree, dragId);
  return !(dragged && isDescendant(dragged, targetId));
};

export const moveNode = (
  tree: ReasonCodeTreeNode[],
  dragId: string,
  targetId: string,
  position: DropPosition,
): ReasonCodeTreeNode[] | null => {
  if (!canDropOn(tree, dragId, targetId)) return null;
  const [without, removed] = removeNode(tree, dragId);
  return removed ? insertNode(without, targetId, position, removed) : null;
};

export const moveNodeToEnd = (tree: ReasonCodeTreeNode[], dragId: string): ReasonCodeTreeNode[] | null => {
  const [without, removed] = removeNode(tree, dragId);
  return removed ? [...without, removed] : null;
};

export const insertNode = (
  tree: ReasonCodeTreeNode[],
  targetId: string,
  position: DropPosition,
  node: ReasonCodeTreeNode,
): ReasonCodeTreeNode[] => {
  if (position === 'inside') {
    return tree.map((n) =>
      n.id === targetId
        ? { ...n, children: [...n.children, node] }
        : { ...n, children: insertNode(n.children, targetId, position, node) },
    );
  }
  const out: ReasonCodeTreeNode[] = [];
  for (const n of tree) {
    if (n.id === targetId) {
      if (position === 'before') out.push(node, n);
      else out.push(n, node);
    } else {
      out.push({ ...n, children: insertNode(n.children, targetId, position, node) });
    }
  }
  return out;
};

export const countTreeNodes = (nodes: ReasonCodeTreeNode[]): number =>
  nodes.reduce((sum, node) => sum + 1 + countTreeNodes(node.children), 0);

export type VisibleTreeRow = { node: ReasonCodeTreeNode; depth: number };

/** Flattens the tree into the rows a viewer of it can currently see, skipping collapsed subtrees. */
export const visibleTreeRows = (tree: ReasonCodeTreeNode[], collapsed: ReadonlySet<string>): VisibleTreeRow[] => {
  const rows: VisibleTreeRow[] = [];
  const visit = (node: ReasonCodeTreeNode, depth: number): void => {
    rows.push({ node, depth });
    if (node.children.length > 0 && !collapsed.has(node.id ?? '')) {
      node.children.forEach((child) => visit(child, depth + 1));
    }
  };
  tree.forEach((node) => visit(node, 0));
  return rows;
};

export const hasUnnamedNodes = (tree: ReasonCodeTreeNode[]): boolean =>
  tree.some((node) => node.name.trim() === '' || hasUnnamedNodes(node.children));

export const hasUnnamedLevels = (levels: ReasonCodeLevel[], roots: ReasonCodeTreeNode[]): boolean =>
  Array.from({ length: maxDepth(roots) }, (_, depth) => depth).some(
    (depth) => (levels[depth]?.name ?? '').trim() === '',
  );

export const maxDepth = (tree: ReasonCodeTreeNode[], depth = 1): number => {
  let max = tree.length ? depth : 0;
  for (const node of tree) {
    if (node.children.length) max = Math.max(max, maxDepth(node.children, depth + 1));
  }
  return max;
};

export const appendLevel = (tree: ReasonCodeTreeNode[]): ReasonCodeTreeNode[] => {
  const deepestTier = maxDepth(tree) - 1;
  const addAt = (nodes: ReasonCodeTreeNode[], depth: number): ReasonCodeTreeNode[] =>
    nodes.map((node) => ({
      ...node,
      children: depth === deepestTier ? [...node.children, makeNode('')] : addAt(node.children, depth + 1),
    }));
  return addAt(tree, 0);
};
